import React, { useEffect, useState } from 'react';
import {
  useAddPovOutcomeMutation,
  useFetchPovOutcomes,
  useFetchPovUseCases,
  useUpdatePovOutcomeMutation
} from '../../../../hooks/opportunity-pov-hooks';
import AIContent from '../../../Common/AIContent';
import { Guide } from '../../../../types/opportunity-pov-items';
import { initPovOutcomes, useFetchPov } from '../../opportunityDetailHelpers';
import { HbrInput, HbrSpinner, HbrTextarea } from '@harbor/elements-react-standalone';
import StbButton from '../../../security-toolbox-ui/StbButton';
import DesignPhaseOutcome from './DesignPhaseOutcome';
import { fetchOrCreateAIContent, createAIResponse } from '../../../../support/aiHelpers';
import { AIResponse } from '../../../../types/aiTypes';
import { useFetchOpportunity, useFetchOpportunityProducts } from '../../../../hooks/opportunity-hooks';
import { useParams } from 'react-router-dom';
import { unicodeToPlain } from '../../../../support/utils';
import { arrowLeft } from '@harbor/elements/icons';
import StbApiModal from '../../../security-toolbox-ui/StbApiModal';
import { useScreenSize } from '../../../../hooks/useScreenSize';
import OpportunityPhaseLayout from '../OpportunityPhaseLayout';
import OpportunityPhaseCard from '../OpportunityPhaseCard';

const MAX_TITLE_LENGTH = 40;
const TITLE_PATTERN = "^[a-zA-Z0-9 ,.\u0027\\-]{1,40}$";
const TITLE_REGEX = /^[a-zA-Z0-9 ,.'-]{1,40}$/;

const DesignPhase: React.FC = () => {
  const { id: opportunityId } = useParams();
  const { data: opportunity } = useFetchOpportunity(opportunityId, { enabled: !!opportunityId });
  const { data: pov, isLoading: povIsLoading } = useFetchPov(opportunity);
  const { data: povOutcomes, isLoading: povOutcomesLoading } = useFetchPovOutcomes(opportunityId);
  const { data: povUsecases, isLoading: povUsecasesLoading } = useFetchPovUseCases(opportunityId);
  const { data: products } = useFetchOpportunityProducts(opportunityId, { enabled: !!opportunityId });

  const [init, setInit] = useState<boolean>(true);
  const [guide, setGuide] = useState<Guide>(null);
  const [aiComponentVisible, setAiComponentVisible] = useState(false);
  const [aiGuide, setAIGuide] = useState<AIResponse | null>(null);
  const [aiGuideLoading, setAIGuideLoading] = useState(false);
  const [aiGuideError, setAIGuideError] = useState<string | null>(null);
  const [aiContextText, setAiContextText] = useState<string>('');
  const [regenerateAIResponse, setRegenerateAIResponse] = useState(false);
  const [currentlyEditing, setCurrentlyEditing] = useState<any>(null);
  const [showValidation, setShowValidation] = useState<boolean>(false);
  const [titleTouched, setTitleTouched] = useState(false);

  const { height: viewportHeight } = useScreenSize();
  const aiContentHeightPx = Math.max(320, viewportHeight - 280);
  const aiContentHeight = `${aiContentHeightPx}px`;

  const { mutateAsync: createOutcome, isPending: outcomeAdding } = useAddPovOutcomeMutation();
  const { mutateAsync: updateOutcome, isPending: outcomeUpdating } = useUpdatePovOutcomeMutation();

  useEffect(() => {
    let cancelled = false;

    const fetchOrCreateGuide = async () => {
      if (!aiComponentVisible || !aiContextText) return;
      setAIGuideError(null);
      setAIGuide(null);
      setAIGuideLoading(true);

      try {
        const context = aiContextText;

        let chat = await fetchOrCreateAIContent(
          'opportunity',
          opportunityId,
          103,
          context,
          '',
          [],
          opportunity
        );

        if (!chat || typeof chat !== 'object' || !('id' in chat)) {
          chat = await createAIResponse(context, 'opportunity', opportunityId, 103, '', []);
        }

        if (!cancelled) {
          if (chat && typeof chat === 'object' && 'id' in chat) {
            setAIGuide(chat as AIResponse);
          } else {
            setAIGuideError('No AI Design Guide could be created.');
          }
        }
      } catch (error) {
        if (!cancelled) setAIGuideError('Error fetching AI Design Guide.');
      } finally {
        if (!cancelled) setAIGuideLoading(false);
      }
    };

    fetchOrCreateGuide();

    return () => {
      cancelled = true;
    };
  }, [aiComponentVisible, aiContextText, opportunityId, opportunity]);

  useEffect(() => {
    if (!povIsLoading && init && opportunity) {
      initPovOutcomes({ ...opportunity, Id: opportunityId });
      setInit(false);
    }
  }, [init, opportunity, povIsLoading, opportunityId]);

  useEffect(() => {
    if (!pov) return;
    if (!guide && !povIsLoading) {
      setGuide({
        ...(guide ?? {}),
        id: `guide-${pov.id}`,
        title: pov.name,
        povId: pov.id,
        outcomes: [],
      });
    }
  }, [pov, povIsLoading, guide]);

  const generateAIDesignContext = () => {
    let checkRulesPassed = true;
    let context = `${opportunity.ai_context}\n\nOutcomes:\n\n`;

    if ((povOutcomes ?? []).length === 0) {
      alert('You must have at least one outcome with a related use case to generate a deployment guide');
      return;
    }

    povOutcomes.forEach((outcome) => {
      const useCases = povUsecases.filter((useCase) => useCase.pov_outcome === outcome.id);
      if (useCases.length === 0) {
        alert('All outcomes must have at least one related use case to generate a deployment guide');
        checkRulesPassed = false;
        return;
      }

      context += `\tOutcome Title: ${outcome.title}\n\t`;
      if (outcome?.description) {
        context += `Description: ${outcome.description}\n\n\tUse Cases:\n\n`;
      }

      useCases.forEach((usecase) => {
        context += `\t\tUse-Case Title: ${usecase.title}\n\t\t`;
        if (usecase?.description) {
          context += `Description: ${usecase.description}\n`;
        }
        const product = products.find((product) => product.id === usecase?.product);
        if (product) {
          context += `\t\t\tSelected Product: ${product.name}\n\n`;
        }
      });
    });

    if (checkRulesPassed) {
      setAiContextText(context);
      setAiComponentVisible(true);
    }
  };

  const regenerateResponse = () => {
    setRegenerateAIResponse(true);
  };

  const isTitleInvalid = (title: string) => {
    if (!title) return true;
    if (title.trim() === '') return true;
    if (title.length > MAX_TITLE_LENGTH) return true;
    return !TITLE_REGEX.test(title);
  };

  const getTitleHelpText = (title: string) => {
    if (!title || title.trim() === '') return 'Outcome name is required';
    if (title.length > MAX_TITLE_LENGTH) return `Outcome name cannot be longer than ${MAX_TITLE_LENGTH} characters`;
    if (!TITLE_REGEX.test(title)) return 'Invalid format';
    return '';
  };

  const handleSave = async () => {
    if (isTitleInvalid(currentlyEditing?.title)) {
      setShowValidation(true);
      setTitleTouched(true);
      return;
    }

    if (currentlyEditing?.id) {
      await updateOutcome(currentlyEditing);
    } else {
      await createOutcome(currentlyEditing);
    }

    regenerateResponse();
    setShowValidation(false);
    setTitleTouched(false);
    setCurrentlyEditing(null);
  };

  const handleClose = async () => {
    setTitleTouched(false);
    setCurrentlyEditing(null);
    setShowValidation(false);
  };

  const outcomesLoading = (): boolean =>
    povOutcomesLoading || outcomeAdding || outcomeUpdating;

  return (
    <OpportunityPhaseLayout
      title="Design Phase"
      subtitle="Define outcomes and use cases, then generate a deployment guide."
      actions={
        !aiComponentVisible ? (
          <StbButton
            loading={povUsecasesLoading || povOutcomesLoading}
            role="request"
            onClick={() => generateAIDesignContext()}
          >
            Generate Design Guide
          </StbButton>
        ) : (
          <StbButton
            onClick={() => setAiComponentVisible(false)}
            icon={arrowLeft}
          >
            Outcomes
          </StbButton>
        )
      }
    >
      <OpportunityPhaseCard title={aiComponentVisible ? 'AI Design Guide' : 'Outcomes'}>
        {guide &&
          (aiComponentVisible && aiContextText ? (
            <>
              {aiGuideLoading && (
                <div>
                  <HbrSpinner size="large">
                    <div slot="label">Loading...</div>
                  </HbrSpinner>
                </div>
              )}
              {aiGuideError && <div style={{ color: 'red' }}>{aiGuideError}</div>}
              {aiGuide && !aiGuideLoading && !aiGuideError && (
                <AIContent
                  related_type="opportunity"
                  related_id={opportunityId}
                  question_id={103}
                  height={aiContentHeight}
                  width="100%"
                  conversation={aiGuide.conversation}
                  context={aiContextText}
                  conversationRequired={true}
                  item={opportunity}
                  regenerate={regenerateAIResponse}
                  pdfTitle={`${opportunity?.Name} Design Guide`}
                />
              )}
            </>
          ) : (
            <>
              {(!povOutcomes || povOutcomes.length === 0) &&
                (outcomesLoading() ? (
                  <HbrSpinner size="medium" />
                ) : (
                  <div><em>No outcomes found</em></div>
                ))}

              {(povOutcomes ?? []).map((povOutcome) => (
                <DesignPhaseOutcome
                  opportunityId={opportunityId}
                  outcome={povOutcome}
                  key={povOutcome.id}
                  editOC={() => setCurrentlyEditing(povOutcome)}
                  updated={regenerateResponse}
                />
              ))}

              {!outcomesLoading() && (
                <div className="mt-4">
                  <StbButton
                    role="add"
                    size="medium"
                    variant="text"
                    onClick={() =>
                      setCurrentlyEditing({
                        title: null,
                        description: null,
                        pov: pov.id,
                      })
                    }
                  >
                    Add Outcome
                  </StbButton>
                </div>
              )}
            </>
          ))}
      </OpportunityPhaseCard>

      <StbApiModal
        open={!!currentlyEditing}
        onSubmit={handleSave}
        onClose={handleClose}
        submitLabel={`${currentlyEditing?.id ? 'Update' : 'Save'} Outcome`}
        submitDisabled={isTitleInvalid(currentlyEditing?.title)}
        submitSuccess={`Outcome ${currentlyEditing?.id ? 'updated' : 'created'} successfully`}
        submitError={`Failed to ${currentlyEditing?.id ? 'update' : 'create'} Outcome`}
        title={`${currentlyEditing?.id ? 'Edit' : 'Add'} Outcome`}
        size="medium"
        role={currentlyEditing?.id ? 'update' : 'save'}
      >
        <div className="flex-col flex">
          <HbrInput
            value={currentlyEditing?.title}
            placeholder="Add an outcome name"
            className="grow required"
            error={(titleTouched || showValidation) && isTitleInvalid(currentlyEditing?.title)}
            maxlength={MAX_TITLE_LENGTH}
            pattern={TITLE_PATTERN}
            onHbr-input={(e: any) => {
              const value = e.target?.value ?? '';
              setCurrentlyEditing({
                ...currentlyEditing,
                title: unicodeToPlain(value),
              });
              if (!titleTouched) setTitleTouched(true);
            }}
            required
          >
            <div slot="label">
              Name <em className="text-gray-500">(required)</em>
            </div>
            {(titleTouched || showValidation) && isTitleInvalid(currentlyEditing?.title) && (
              <div slot="help-text" className="text-sm mt-1 text-danger">
                {getTitleHelpText(currentlyEditing?.title)}
              </div>
            )}
          </HbrInput>

          <HbrTextarea
            value={currentlyEditing?.description}
            onHbr-input={(e: any) =>
              setCurrentlyEditing({
                ...currentlyEditing,
                description: unicodeToPlain(e.target?.value ?? ''),
              })
            }
            label="Description"
          />
        </div>
      </StbApiModal>
    </OpportunityPhaseLayout>
  );
};

export default DesignPhase;